/**
 * useDrivingMode — Background location tracking for eCaution alerts.
 *
 * When enabled:
 *   - Acquires a Screen Wake Lock so the device doesn't sleep mid-trip.
 *   - Subscribes to navigator.geolocation.watchPosition (high accuracy).
 *   - Throttles location syncs to the backend (every 30s OR every 75m moved)
 *     so the user's stored location stays fresh for proximity-based pushes.
 *   - On every fresh position, scans the supplied alerts list for any
 *     `ecaution` alert within the user's configured eCaution proximity radius
 *     that hasn't already been spoken this session, and fires playECaution()
 *     plus a toast. The "spoken" set is keyed by alert_id so each pothole /
 *     checkpoint announces itself exactly once per driving session.
 *
 * When disabled (or component unmounts):
 *   - Clears the watch handle.
 *   - Releases the wake lock.
 *
 * Persists the on/off preference in localStorage("driving_mode_on").
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { playECaution, playSpokenAlert } from "../utils/alertSound";
import { distanceMeters } from "../utils/geo";
import logging from "../utils/logging";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SYNC_INTERVAL_MS = 30000;     // sync to backend at least every 30s
const SYNC_DISTANCE_M = 75;         // …or whenever the user moves 75m
const STORAGE_KEY = "driving_mode_on";
const SPEED_LIMIT_KEY = "driving_speed_limit_kph";
const DEFAULT_SPEED_LIMIT_KPH = 120;
// Only announce over-speed AFTER the driver has been above the limit for this long.
// Stops single-sample GPS jitter from triggering false alarms on a clean 119 km/h cruise.
const OVERSPEED_CONFIRM_MS = 2500;
// Don't re-announce until this much time has passed since the last alarm —
// it's a safety nudge, not a running harangue.
const OVERSPEED_REANNOUNCE_MS = 25000;

export function useDrivingMode({ alerts, ecautionRadiusKm, onLocationUpdate }) {
  // Restore prior session preference (best-effort; localStorage may throw in
  // private mode on some browsers).
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch { return false; }
  });
  const [currentSpeedKph, setCurrentSpeedKph] = useState(null);
  const [trackedSince, setTrackedSince] = useState(null);
  const [speedLimitKph, setSpeedLimitKphState] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(SPEED_LIMIT_KEY) || "", 10);
      return Number.isFinite(saved) && saved >= 30 && saved <= 250 ? saved : DEFAULT_SPEED_LIMIT_KPH;
    } catch { return DEFAULT_SPEED_LIMIT_KPH; }
  });
  const [isOverSpeed, setIsOverSpeed] = useState(false);

  // Expose a setter that also persists the choice
  const setSpeedLimitKph = useCallback((kph) => {
    const clamped = Math.max(30, Math.min(250, Math.round(kph) || DEFAULT_SPEED_LIMIT_KPH));
    setSpeedLimitKphState(clamped);
    try { localStorage.setItem(SPEED_LIMIT_KEY, String(clamped)); } catch { /* private mode / quota — preference just won't persist across sessions */ }
  }, []);

  const watchIdRef = useRef(null);
  const wakeLockRef = useRef(null);
  const lastSyncedPosRef = useRef(null);
  const lastSyncedAtRef = useRef(0);
  const spokenIdsRef = useRef(new Set()); // alert_ids already announced this session
  const alertsRef = useRef(alerts);
  const radiusRef = useRef(ecautionRadiusKm);
  const speedLimitRef = useRef(speedLimitKph);
  const overSpeedSinceRef = useRef(null); // timestamp when current over-speed streak began
  const lastOverSpeedAlarmRef = useRef(0);

  // Keep the latest alerts/radius accessible to the watchPosition callback
  // without having to re-subscribe every time they change.
  useEffect(() => { alertsRef.current = alerts; }, [alerts]);
  useEffect(() => { radiusRef.current = ecautionRadiusKm; }, [ecautionRadiusKm]);
  useEffect(() => { speedLimitRef.current = speedLimitKph; }, [speedLimitKph]);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch { /* private mode — driving mode still works, just won't persist */ }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;

    if (!("geolocation" in navigator)) {
      toast.error("Geolocation is not supported on this device");
      setEnabled(false);
      return;
    }

    // 1) Wake Lock (best-effort; fails silently on Safari iOS < 16.4)
    (async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        }
      } catch (err) {
        logging.warn("Wake lock denied", err);
      }
    })();

    setTrackedSince(Date.now());
    spokenIdsRef.current = new Set();

    // 2) Continuous GPS watch
    const handlePosition = (pos) => {
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const speedMs = pos.coords.speed; // may be null on desktop
      let kph = null;
      if (typeof speedMs === "number" && !isNaN(speedMs)) {
        kph = speedMs * 3.6;
        setCurrentSpeedKph(kph);
      }
      const now = { latitude: lat, longitude: lng };

      // 2a) Over-speed detection — needs a sustained streak of over-limit
      // samples before we alarm, so a brief GPS spike doesn't trigger it.
      const limit = speedLimitRef.current;
      const tNow = Date.now();
      if (kph !== null && limit > 0) {
        if (kph > limit) {
          if (!overSpeedSinceRef.current) overSpeedSinceRef.current = tNow;
          const sustained = tNow - overSpeedSinceRef.current >= OVERSPEED_CONFIRM_MS;
          const cooldownOver = tNow - lastOverSpeedAlarmRef.current >= OVERSPEED_REANNOUNCE_MS;
          if (sustained) {
            setIsOverSpeed(true);
            if (cooldownOver) {
              lastOverSpeedAlarmRef.current = tNow;
              const spoken = Math.round(kph);
              // Localised spoken alert — "Caution! Your speed is 135 kilometres per hour"
              // renders in the user's chosen TTS language (Yoruba / Hausa / Igbo / Pidgin).
              try {
                playSpokenAlert("ecaution", `Your speed is ${spoken} kilometres per hour. Slow down.`);
              } catch (err) { logging.warn("Over-speed TTS failed", err); }
              toast.warning(`Caution! Your speed is ${spoken} km/h`, {
                description: `Limit ${limit} km/h. Slow down.`,
                duration: 6000,
              });
            }
          }
        } else {
          // Dropped back under the limit — reset the streak timer so the
          // next breach starts fresh.
          overSpeedSinceRef.current = null;
          setIsOverSpeed(false);
        }
      }

      // 2b) Throttled backend sync — distance OR time threshold
      const moved = lastSyncedPosRef.current
        ? distanceMeters(lastSyncedPosRef.current, now)
        : Infinity;
      const elapsed = tNow - lastSyncedAtRef.current;
      if (moved >= SYNC_DISTANCE_M || elapsed >= SYNC_INTERVAL_MS) {
        lastSyncedPosRef.current = now;
        lastSyncedAtRef.current = tNow;
        if (onLocationUpdate) onLocationUpdate(now);
        axios.post(`${API}/users/location`, now, { withCredentials: true })
          .catch(() => {}); // non-fatal
      }

      // 2c) Scan eCautions in proximity
      const radiusM = (radiusRef.current ?? 0.5) * 1000;
      const list = alertsRef.current || [];
      for (const a of list) {
        if (a.alert_type !== "ecaution") continue;
        if (a.status && a.status !== "active") continue;
        if (spokenIdsRef.current.has(a.alert_id)) continue;
        const d = distanceMeters(now, { latitude: a.latitude, longitude: a.longitude });
        if (d <= radiusM) {
          spokenIdsRef.current.add(a.alert_id);
          const desc = a.description || "Caution ahead";
          // TTS reads the caution (uses user's selected eCaution voice locale)
          // Pass the exact distance so the voice says e.g. "350 metres from you"
          try { playECaution(desc, d); } catch (err) { logging.warn("TTS failed", err); }
          // Visible confirmation
          toast.message(`Caution: ${desc}`, {
            description: `${Math.round(d)} m ahead`,
            duration: 6000,
          });
        }
      }
    };

    const handleError = (err) => {
      logging.warn("watchPosition error", err);
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      handlePosition,
      handleError,
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 }
    );

    toast.success("Driving Mode on — listening for nearby cautions");

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {});
        wakeLockRef.current = null;
      }
      setTrackedSince(null);
      setCurrentSpeedKph(null);
      setIsOverSpeed(false);
      overSpeedSinceRef.current = null;
      lastOverSpeedAlarmRef.current = 0;
    };
  }, [enabled, onLocationUpdate]);

  // Re-acquire wake lock if the user comes back to the tab (browsers release
  // it on visibility change). No-op when driving mode is off.
  useEffect(() => {
    if (!enabled) return;
    const reacquire = async () => {
      if (document.visibilityState === "visible" && !wakeLockRef.current && "wakeLock" in navigator) {
        try { wakeLockRef.current = await navigator.wakeLock.request("screen"); } catch { /* wake-lock denied on iOS Safari < 16.4 / cross-origin frames — degrade to screen-off */ }
      }
    };
    document.addEventListener("visibilitychange", reacquire);
    return () => document.removeEventListener("visibilitychange", reacquire);
  }, [enabled]);

  return {
    enabled,
    toggle,
    currentSpeedKph,
    trackedSince,
    speedLimitKph,
    setSpeedLimitKph,
    isOverSpeed,
  };
}
